import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useApi, api, uploadUrl } from '../hooks/useApi';
import DateInput from '../components/DateInput';
import Icon from '../components/Icon';
import './InventaireMatieres.css';

// ─── Constantes ───────────────────────────────
const CATEGORIES = [
  { key: 'tous',      label: 'Tout' },
  { key: 'viande',    label: 'Viandes' },
  { key: 'epices',    label: 'Épices' },
  { key: 'laitier',   label: 'Laitier' },
  { key: 'emballage', label: 'Emballage' },
  { key: 'alcool',    label: 'Alcool' },
  { key: 'legume',    label: 'Légumes' },
  { key: 'etiquette', label: 'Étiquettes' },
  { key: 'bois',      label: 'Bois' },
  { key: 'autre',     label: 'Autre' },
];
const CATEGORIES_OPTIONS = ['viande','epices','laitier','emballage','alcool','legume','etiquette','bois','autre'];
const UNITES_OPTIONS     = ['kg','g','litres','ml','cup','unites','boites','sacs'];

// Mapping produit → clé Icon (pas d'emojis)
const PRODUIT_ICON = {
  saucisses:    'saucisses',
  porc:         'porc',
  cotes_levees: 'cotes_levees',
  saumon:       'saumon',
  brisket:      'brisket',
  sauce:        'sauce',
};
const PRODUIT_LABEL = {
  saucisses:    'Saucisses',
  porc:         'Porc Effiloché',
  cotes_levees: 'Côtes Levées',
  saumon:       'Saumon Fumé',
  brisket:      'Brisket',
  sauce:        'Sauce BBQ',
};

const PRODUITS_RECETTES = [
  { produit: 'saucisses',    label: 'Saucisses',       icon: 'saucisses'    },
  { produit: 'porc',         label: 'Porc Effiloché',  icon: 'porc'         },
  { produit: 'cotes_levees', label: 'Côtes Levées',    icon: 'cotes_levees' },
  { produit: 'saumon',       label: 'Saumon Fumé',     icon: 'saumon'       },
  { produit: 'brisket',      label: 'Brisket',         icon: 'brisket'      },
  { produit: 'sauce',        label: 'Sauce BBQ',       icon: 'sauce'        },
];

const POIDS_VIANDE_PAR_PRODUIT = {
  saucisses:    31.63,
  porc:         20,
  cotes_levees: 30,
  saumon:       15,
  brisket:      15,
  sauce:        0,
};

function statutBadge(item) {
  if (item.seuil_alerte > 0 && item.quantite === 0)
    return <span className="inv-badge inv-badge--vide"><Icon name="erreur" size={10} />Vide</span>;
  if (item.seuil_alerte > 0 && item.quantite <= item.seuil_alerte)
    return <span className="inv-badge inv-badge--bas"><Icon name="attention" size={10} />Bas</span>;
  return <span className="inv-badge inv-badge--ok"><Icon name="check" size={10} />OK</span>;
}

function couleurBatch(n) {
  if (n === 0) return 'danger';
  if (n === 1) return 'warning';
  return 'success';
}

// ─── Modal Prédiction (avec ✏ par ingrédient) ──
function ModalPrediction({ pred, onClose, onAjustIngredient }) {
  if (!pred) return null;
  return (
    <div className="inv-overlay" onClick={onClose}>
      <div className="inv-modal" onClick={e => e.stopPropagation()}>
        <div className="inv-modal__header">
          <h3>{PRODUIT_LABEL[pred.produit] || pred.produit}</h3>
          <button className="inv-modal__close" onClick={onClose} aria-label="Fermer"><Icon name="fermer" size={14} /></button>
        </div>
        <div className="inv-modal__body">
          <p className={`inv-modal__batches inv-modal__batches--${couleurBatch(pred.nb_batches_possible)}`}>
            {pred.nb_batches_possible} batch{pred.nb_batches_possible !== 1 ? 'es' : ''} possible{pred.nb_batches_possible !== 1 ? 's' : ''}
          </p>
          <p className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
            Cliquez sur une ligne pour ajuster le stock de cet ingrédient.
          </p>
          <table className="esc-table">
            <thead>
              <tr>
                <th>Ingrédient</th>
                <th>Stock</th>
                <th>Besoin/batch</th>
                <th>Batches</th>
              </tr>
            </thead>
            <tbody>
              {pred.ingredients.map((ing, i) => (
                <tr
                  key={i}
                  className={`inv-pred-ing-row ${ing.suffisant ? '' : 'inv-row--alerte'}`}
                  onClick={() => onAjustIngredient(ing.id)}
                  title="Cliquer pour ajuster le stock"
                >
                  <td>
                    <span className="inv-pred-ing-nom">{ing.nom}</span>
                    <span className="inv-pred-ing-edit"><Icon name="modifier" size={12} /></span>
                  </td>
                  <td>{ing.stock_actuel} {ing.unite_stock}</td>
                  <td>{ing.besoin_par_batch} {ing.unite}
                    {ing.type_calcul === 'pct_viande' && <span className="muted"> (% viande)</span>}
                  </td>
                  <td>
                    <span className={ing.suffisant ? 'success' : 'danger'}>
                      {ing.batches_possibles >= 999 ? '∞' : ing.batches_possibles}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const RAISONS_AJUSTEMENT = [
  { value: "Inventaire d'ouverture",  label: "Inventaire d'ouverture",  signe: '+' },
  { value: 'Réception de don',        label: 'Réception de don',        signe: '+' },
  { value: 'Correction à la hausse',  label: 'Correction à la hausse',  signe: '+' },
  { value: 'Perte / Bris',            label: 'Perte / Bris',            signe: '-' },
  { value: 'Correction à la baisse',  label: 'Correction à la baisse',  signe: '-' },
  { value: 'Inventaire physique',     label: 'Inventaire physique',     signe: '±' },
];

// ─── Modal Achat complet (depuis drawer) ─────
function ModalAchat({ matiere, onClose, onSuccess }) {
  const [mode, setMode]             = useState('achat');
  const [form, setForm]             = useState({
    date_achat:     new Date().toISOString().split('T')[0],
    quantite:       '',
    prix_unitaire:  matiere?.prix_unitaire || '',
    fournisseur:    matiere?.fournisseur   || '',
    numero_facture: '',
    raison:         "Inventaire d'ouverture",
    notes:          '',
  });
  const [fichier, setFichier]           = useState(null);
  const [preview, setPreview]           = useState(null);
  const [saving, setSaving]             = useState(false);
  const [erreur, setErreur]             = useState('');
  const [dragOver, setDragOver]         = useState(false);
  const [confirmNegatif, setConfirmNegatif] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFile = (file) => {
    if (!file) return;
    setFichier(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = e => setPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const qteNum       = parseFloat(form.quantite) || 0;
  const stockActuel  = matiere?.quantite ?? 0;
  const nouveauStock = Math.round((stockActuel + qteNum) * 1000) / 1000;
  const stockNegatif = mode === 'ajustement' && qteNum < 0 && nouveauStock < 0;
  const cout = mode === 'achat' && form.quantite && form.prix_unitaire
    ? Math.round(parseFloat(form.quantite) * parseFloat(form.prix_unitaire) * 100) / 100
    : null;

  const doSubmit = async () => {
    setSaving(true);
    setErreur('');
    try {
      const data = new FormData();
      data.append('date_achat', form.date_achat);
      data.append('quantite',   form.quantite);
      data.append('notes',      form.notes);
      data.append('type',       mode);
      if (mode === 'achat') {
        data.append('prix_unitaire',  form.prix_unitaire);
        data.append('fournisseur',    form.fournisseur);
        data.append('numero_facture', form.numero_facture);
        if (fichier) data.append('fichier', fichier);
      } else {
        data.append('raison', form.raison);
      }
      await api.post(`/inventaire/matieres/${matiere.id}/achat`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSuccess();
    } catch (err) {
      setErreur(err.response?.data?.error || "Erreur lors de l'enregistrement.");
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErreur('');
    if (mode === 'achat') {
      if (!form.date_achat || !form.quantite || !form.prix_unitaire) {
        setErreur('Date, quantité et prix unitaire sont requis.');
        return;
      }
    } else {
      if (!form.date_achat || !form.quantite) {
        setErreur('Date et quantité sont requis.');
        return;
      }
      if (qteNum === 0) { setErreur('La quantité ne peut pas être zéro.'); return; }
      if (stockNegatif) { setConfirmNegatif(true); return; }
    }
    await doSubmit();
  };

  return (
    <div className="inv-overlay" onClick={onClose}>
      <div className="inv-modal inv-modal--achat" onClick={e => e.stopPropagation()}>
        <div className="inv-modal__header">
          <h3>{mode === 'achat' ? 'Achat' : 'Ajustement'} — {matiere?.nom}</h3>
          <button className="inv-modal__close" onClick={onClose} aria-label="Fermer"><Icon name="fermer" size={14} /></button>
        </div>
        <form className="inv-modal__body" onSubmit={handleSubmit}>
          <div className="inv-mode-toggle">
            <button type="button" className={`inv-mode-btn ${mode === 'achat' ? 'inv-mode-btn--active' : ''}`} onClick={() => { setMode('achat'); setErreur(''); setConfirmNegatif(false); }}>Achat</button>
            <button type="button" className={`inv-mode-btn ${mode === 'ajustement' ? 'inv-mode-btn--active' : ''}`} onClick={() => { setMode('ajustement'); setErreur(''); setConfirmNegatif(false); }}>Ajustement / Inventaire d'ouverture</button>
          </div>
          {mode === 'achat' ? (
            <p className="inv-mode-info inv-mode-info--achat">Une écriture comptable sera créée automatiquement.</p>
          ) : (
            <p className="inv-mode-info inv-mode-info--ajustement">Aucune écriture comptable ne sera créée. Utilisé pour l'inventaire initial, une correction ou une perte.</p>
          )}
          <div className="inv-form-grid">
            <label>Date<DateInput value={form.date_achat} onChange={v => set('date_achat', v)} required /></label>
            <label>Quantité ({matiere?.unite}){mode === 'ajustement' && <span className="muted"> — négatif pour une perte</span>}
              <input type="number" step="0.001" min={mode === 'achat' ? '0.001' : undefined} placeholder={mode === 'ajustement' ? 'ex: 5 ou -2.5' : 'ex: 5'} value={form.quantite} onChange={e => set('quantite', e.target.value)} required />
            </label>
            {mode === 'ajustement' && form.quantite !== '' && (
              <div className="inv-stock-apercu" style={{ gridColumn: '1 / -1' }}>
                <span className="muted">Stock actuel :</span> <strong>{stockActuel} {matiere?.unite}</strong>
                <span className="muted" style={{ margin: '0 8px' }}>→</span>
                <strong className={nouveauStock < 0 ? 'danger' : 'success'}>{nouveauStock} {matiere?.unite}</strong>
              </div>
            )}
            {mode === 'achat' && (<>
              <label>Prix unitaire ($ / {matiere?.unite_prix || matiere?.unite})<input type="number" step="0.01" min="0" placeholder="ex: 8.50" value={form.prix_unitaire} onChange={e => set('prix_unitaire', e.target.value)} required /></label>
              <label>Fournisseur<input type="text" placeholder="ex: Costco, Metro..." value={form.fournisseur} onChange={e => set('fournisseur', e.target.value)} /></label>
              <label>Numéro de facture<input type="text" placeholder="ex: INV-2024-001" value={form.numero_facture} onChange={e => set('numero_facture', e.target.value)} /></label>
            </>)}
            {mode === 'ajustement' && (
              <label style={{ gridColumn: '1 / -1' }}>Raison de l'ajustement
                <select value={form.raison} onChange={e => set('raison', e.target.value)} required>
                  {RAISONS_AJUSTEMENT.map(r => (<option key={r.value} value={r.value}>{r.signe === '+' ? '(+) ' : r.signe === '-' ? '(−) ' : '(±) '}{r.label}</option>))}
                </select>
              </label>
            )}
            <label style={mode === 'achat' ? {} : { gridColumn: '1 / -1' }}>Notes<input type="text" placeholder="Optionnel" value={form.notes} onChange={e => set('notes', e.target.value)} /></label>
          </div>
          {mode === 'achat' && cout !== null && (
            <div className="inv-apercu-ecriture">
              <span className="muted">Écriture comptable générée :</span>
              <span className="danger"> −{cout}$ </span>
              <span className="muted">— Dépense / Achat viande &amp; intrants</span>
            </div>
          )}
          {mode === 'achat' && (
            <>
              <div className={`inv-drop-zone ${dragOver ? 'inv-drop-zone--over' : ''} ${fichier ? 'inv-drop-zone--has-file' : ''}`}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
                onClick={() => document.getElementById('inv-file-input').click()}>
                {preview ? (<img src={preview} alt="Aperçu facture" className="inv-drop-zone__preview" />)
                  : fichier ? (<div className="inv-drop-zone__pdf"><Icon name="document" size={16} /> {fichier.name}</div>)
                  : (<div className="inv-drop-zone__placeholder"><span className="inv-drop-icon"><Icon name="televerser" size={24} /></span><p>Glisser une facture ici ou cliquer</p><small>.pdf, .jpg, .jpeg, .png — max 10 Mo</small></div>)}
                <input id="inv-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
                <input id="inv-file-camera" type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
              </div>
              <div className="inv-mobile-upload">
                <button type="button" className="btn btn-ghost btn--sm" onClick={() => document.getElementById('inv-file-camera').click()}><Icon name="photo" size={14} /> Prendre une photo</button>
                <button type="button" className="btn btn-ghost btn--sm" onClick={() => document.getElementById('inv-file-input').click()}><Icon name="document" size={14} /> Choisir un fichier</button>
              </div>
            </>
          )}
          {confirmNegatif && (
            <div className="inv-confirm-negatif">
              <p className="warning">Ce retrait amènerait le stock à <strong>{nouveauStock} {matiere?.unite}</strong> — en dessous de zéro. Continuer quand même ?</p>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button type="button" className="btn btn-ghost" onClick={() => setConfirmNegatif(false)}>Non</button>
                <button type="button" className="btn btn-danger" disabled={saving} onClick={doSubmit}>{saving ? 'Enregistrement…' : 'Oui, confirmer'}</button>
              </div>
            </div>
          )}
          {erreur && <p className="danger" style={{ marginTop: 8 }}>{erreur}</p>}
          {!confirmNegatif && (
            <div className="inv-modal__actions">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Annuler</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Enregistrement…' : mode === 'achat' ? "Enregistrer l'achat" : "Enregistrer l'ajustement"}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

// ─── Modal Créer une matière ──────────────────
function ModalCreer({ onClose, onSuccess }) {
  const [form, setForm] = useState({
    nom: '', categorie: 'viande', quantite: '0',
    unite: 'kg', prix_unitaire: '0', unite_prix: 'kg',
    seuil_alerte: '0', fournisseur: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim()) { setErreur('Le nom est requis.'); return; }
    setSaving(true);
    setErreur('');
    try {
      await api.post('/inventaire/matieres', form);
      onSuccess();
    } catch (err) {
      setErreur(err.response?.data?.error || 'Erreur lors de la création.');
      setSaving(false);
    }
  };

  return (
    <div className="inv-overlay" onClick={onClose}>
      <div className="inv-modal inv-modal--achat" onClick={e => e.stopPropagation()}>
        <div className="inv-modal__header">
          <h3>Nouvelle matière première</h3>
          <button className="inv-modal__close" onClick={onClose} aria-label="Fermer"><Icon name="fermer" size={14} /></button>
        </div>
        <form className="inv-modal__body" onSubmit={handleSubmit}>
          <div className="inv-form-grid">
            <label style={{ gridColumn: '1 / -1' }}>Nom
              <input type="text" placeholder="ex: Sel Kasher" value={form.nom} onChange={e => set('nom', e.target.value)} required autoFocus />
            </label>
            <label>Catégorie
              <select value={form.categorie} onChange={e => set('categorie', e.target.value)}>
                {CATEGORIES_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label>Unité
              <select value={form.unite} onChange={e => set('unite', e.target.value)}>
                {UNITES_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </label>
            <label>Quantité initiale
              <input type="number" step="0.001" min="0" value={form.quantite} onChange={e => set('quantite', e.target.value)} />
            </label>
            <label>Prix unitaire ($)
              <input type="number" step="0.01" min="0" placeholder="ex: 8.50" value={form.prix_unitaire} onChange={e => set('prix_unitaire', e.target.value)} />
            </label>
            <label>Unité du prix
              <select value={form.unite_prix} onChange={e => set('unite_prix', e.target.value)}>
                {UNITES_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </label>
            <label>Seuil d'alerte
              <input type="number" step="0.01" min="0" value={form.seuil_alerte} onChange={e => set('seuil_alerte', e.target.value)} />
            </label>
            <label>Fournisseur
              <input type="text" placeholder="ex: Costco" value={form.fournisseur} onChange={e => set('fournisseur', e.target.value)} />
            </label>
            <label style={{ gridColumn: '1 / -1' }}>Notes
              <input type="text" placeholder="Optionnel" value={form.notes} onChange={e => set('notes', e.target.value)} />
            </label>
          </div>
          {erreur && <p className="danger" style={{ marginTop: 8 }}>{erreur}</p>}
          <div className="inv-modal__actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Création…' : 'Créer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Modal Édition des infos ──────────────────
function ModalEditInfos({ matiere, onClose, onSuccess, onArchiver }) {
  const [form, setForm] = useState({
    nom:                   matiere?.nom                   || '',
    categorie:             matiere?.categorie             || 'autre',
    unite:                 matiere?.unite                 || 'kg',
    prix_unitaire:         matiere?.prix_unitaire         || '0',
    unite_prix:            matiere?.unite_prix            || 'kg',
    seuil_alerte:          matiere?.seuil_alerte          ?? '0',
    fournisseur:           matiere?.fournisseur           || '',
    notes:                 matiere?.notes                 || '',
    est_frais:             matiere?.est_frais             ? true : false,
    categorie_tracabilite: matiere?.categorie_tracabilite || 'viande',
  });
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim()) { setErreur('Le nom est requis.'); return; }
    setSaving(true);
    setErreur('');
    try {
      await api.put(`/inventaire/matieres/${matiere.id}`, form);
      onSuccess();
    } catch (err) {
      setErreur(err.response?.data?.error || "Erreur lors de la sauvegarde.");
      setSaving(false);
    }
  };

  const handleArchiver = async () => {
    setSaving(true);
    try {
      await api.patch(`/inventaire/matieres/${matiere.id}/archiver`);
      onArchiver();
    } catch (err) {
      setErreur(err.response?.data?.error || "Erreur lors de l'archivage.");
      setSaving(false);
    }
  };

  return (
    <div className="inv-overlay" onClick={onClose}>
      <div className="inv-modal inv-modal--achat" onClick={e => e.stopPropagation()}>
        <div className="inv-modal__header">
          <h3>Modifier — {matiere?.nom}</h3>
          <button className="inv-modal__close" onClick={onClose} aria-label="Fermer"><Icon name="fermer" size={14} /></button>
        </div>
        <form className="inv-modal__body" onSubmit={handleSubmit}>
          <div className="inv-form-grid">
            <label style={{ gridColumn: '1 / -1' }}>Nom
              <input type="text" value={form.nom} onChange={e => set('nom', e.target.value)} required autoFocus />
            </label>
            <label>Catégorie
              <select value={form.categorie} onChange={e => set('categorie', e.target.value)}>
                {CATEGORIES_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label>Unité
              <select value={form.unite} onChange={e => set('unite', e.target.value)}>
                {UNITES_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </label>
            <label>Prix unitaire ($)
              <input type="number" step="0.01" min="0" value={form.prix_unitaire} onChange={e => set('prix_unitaire', e.target.value)} />
            </label>
            <label>Unité du prix
              <select value={form.unite_prix} onChange={e => set('unite_prix', e.target.value)}>
                {UNITES_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </label>
            <label>Seuil d'alerte
              <input type="number" step="0.01" min="0" value={form.seuil_alerte} onChange={e => set('seuil_alerte', e.target.value)} />
            </label>
            <label>Fournisseur
              <input type="text" placeholder="ex: Costco" value={form.fournisseur} onChange={e => set('fournisseur', e.target.value)} />
            </label>
            <label style={{ gridColumn: '1 / -1' }}>Notes
              <input type="text" placeholder="Optionnel" value={form.notes} onChange={e => set('notes', e.target.value)} />
            </label>
            <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 0 }}>
                <input
                  type="checkbox"
                  checked={form.est_frais}
                  onChange={e => set('est_frais', e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: 'var(--accent)' }}
                />
                <span>Ingrédient frais — traçabilité requise par batch</span>
              </label>
              {form.est_frais && (
                <div style={{ marginTop: 8 }}>
                  <label style={{ marginBottom: 0 }}>Catégorie traçabilité
                    <select value={form.categorie_tracabilite || 'viande'} onChange={e => set('categorie_tracabilite', e.target.value)}>
                      <option value="viande">Viande</option>
                      <option value="produit_laitier">Produit laitier</option>
                      <option value="frais_legume">Légume frais</option>
                      <option value="frais_autre">Autre frais</option>
                    </select>
                  </label>
                </div>
              )}
            </div>
          </div>
          {erreur && <p className="danger" style={{ marginTop: 8 }}>{erreur}</p>}
          <div className="inv-modal__actions">
            <button type="button" className="btn btn-ghost" style={{ marginRight: 'auto', color: 'var(--warning)' }} onClick={handleArchiver} disabled={saving}>Archiver</button>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Sauvegarde…' : 'Sauvegarder'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Toast ────────────────────────────────────
function Toast({ msg, type, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3500);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return <div className={`inv-toast inv-toast--${type || 'success'}`}>{msg}</div>;
}

// ─── Modal Recette Ingrédient (Ajouter / Modifier) ─
function ModalRecetteIngredient({ produit, produitLabel, item, matieres, onClose, onSuccess }) {
  const isEdit = !!item;
  const [form, setForm] = useState({
    matiere_id:        item?.matiere_id        ?? '',
    type_calcul:       item?.type_calcul       || 'fixe',
    quantite_par_batch:item?.quantite_par_batch ?? '',
    unite:             item?.unite             || 'kg',
    pct_base:          item?.pct_base          ?? '',
    notes:             item?.notes             || '',
  });
  const [saving, setSaving]   = useState(false);
  const [erreur, setErreur]   = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const poidsViande  = POIDS_VIANDE_PAR_PRODUIT[produit] || 20;
  const besoin = form.type_calcul === 'pct_viande'
    ? poidsViande * (parseFloat(form.pct_base) / 100)
    : parseFloat(form.quantite_par_batch) || 0;

  const matiereSelected = matieres?.find(m => m.id === parseInt(form.matiere_id));
  const stockActuel     = matiereSelected?.quantite || 0;
  const batchesPossibles = besoin > 0 ? Math.floor(stockActuel / besoin) : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isEdit && !form.matiere_id) { setErreur('Veuillez choisir un ingrédient.'); return; }
    if (form.type_calcul === 'pct_viande') {
      if (!form.pct_base || parseFloat(form.pct_base) <= 0) { setErreur('Le pourcentage doit être > 0.'); return; }
    } else {
      if (!form.quantite_par_batch || parseFloat(form.quantite_par_batch) <= 0) { setErreur('La quantité doit être > 0.'); return; }
    }
    setSaving(true); setErreur('');
    try {
      const body = {
        quantite_par_batch: form.type_calcul === 'pct_viande' ? 0 : parseFloat(form.quantite_par_batch),
        unite:       form.unite,
        type_calcul: form.type_calcul,
        pct_base:    form.type_calcul === 'pct_viande' ? parseFloat(form.pct_base) : null,
        notes:       form.notes || null,
      };
      if (isEdit) {
        await api.put(`/inventaire/recettes/${item.id}`, body);
      } else {
        await api.post('/inventaire/recettes', { ...body, matiere_id: parseInt(form.matiere_id), produit });
      }
      onSuccess();
    } catch (err) {
      setErreur(err.response?.data?.error || 'Erreur lors de la sauvegarde.');
      setSaving(false);
    }
  };

  return (
    <div className="inv-overlay" onClick={onClose}>
      <div className="inv-modal inv-modal--achat" onClick={e => e.stopPropagation()}>
        <div className="inv-modal__header">
          <h3>{isEdit ? `Modifier — ${item.nom_matiere}` : `Ajouter à la recette ${produitLabel}`}</h3>
          <button className="inv-modal__close" onClick={onClose} aria-label="Fermer"><Icon name="fermer" size={14} /></button>
        </div>
        <form className="inv-modal__body" onSubmit={handleSubmit}>
          {!isEdit && (
            <label style={{ display: 'block', marginBottom: 16 }}>
              Ingrédient
              <select value={form.matiere_id} onChange={e => set('matiere_id', e.target.value)} required style={{ display: 'block', width: '100%', marginTop: 4 }}>
                <option value="">— Choisir un ingrédient —</option>
                {(matieres || []).map(m => (
                  <option key={m.id} value={m.id}>{m.nom} ({m.categorie}) — {m.quantite} {m.unite}</option>
                ))}
              </select>
            </label>
          )}

          <div style={{ marginBottom: 16 }}>
            <p className="muted" style={{ fontSize: '0.85rem', marginBottom: 8 }}>Type de calcul</p>
            <div className="inv-ajust-types">
              {[
                { key: 'fixe',      label: 'Fixe' },
                { key: 'pct_viande',label: '% du poids viande' },
                { key: 'par_unite', label: 'Par unité produite' },
              ].map(t => (
                <label key={t.key} className={`inv-ajust-type ${form.type_calcul === t.key ? 'inv-ajust-type--active' : ''}`}>
                  <input type="radio" name="type_calcul" value={t.key} checked={form.type_calcul === t.key} onChange={() => set('type_calcul', t.key)} />
                  {t.label}
                </label>
              ))}
            </div>
          </div>

          <div className="inv-form-grid">
            {(form.type_calcul === 'fixe' || form.type_calcul === 'par_unite') && (<>
              <label>Quantité / batch
                <input type="number" step="0.001" min="0.001" value={form.quantite_par_batch} onChange={e => set('quantite_par_batch', e.target.value)} required autoFocus={isEdit} />
              </label>
              <label>Unité
                <select value={form.unite} onChange={e => set('unite', e.target.value)}>
                  {UNITES_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </label>
            </>)}
            {form.type_calcul === 'pct_viande' && (<>
              <label>Pourcentage (%)
                <input type="number" step="0.01" min="0.01" value={form.pct_base} onChange={e => set('pct_base', e.target.value)} required autoFocus />
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 2 }}>
                <span className="muted" style={{ fontSize: '0.8rem' }}>Quantité estimée / batch</span>
                <strong>{besoin > 0 ? `~${Math.round(besoin * 1000) / 1000} kg` : '—'}</strong>
                <span className="muted" style={{ fontSize: '0.75rem' }}>Base viande : {poidsViande} kg</span>
              </div>
            </>)}
            <label style={{ gridColumn: '1 / -1' }}>Notes
              <input type="text" placeholder="Optionnel" value={form.notes} onChange={e => set('notes', e.target.value)} />
            </label>
          </div>

          {matiereSelected && besoin > 0 && (
            <div className="inv-recette-impact">
              <span className="muted">Impact sur prédictions :</span>
              <span> {stockActuel} {matiereSelected.unite} en stock → </span>
              <span className={batchesPossibles >= 1 ? 'success' : 'danger'}>
                {batchesPossibles} batch{batchesPossibles !== 1 ? 'es' : ''} possible{batchesPossibles !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          {erreur && <p className="danger" style={{ marginTop: 8 }}>{erreur}</p>}
          <div className="inv-modal__actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Enregistrement…' : isEdit ? 'Sauvegarder' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Confirm suppression ingrédient recette ───
function ConfirmDeleteIngredient({ ing, isLimitant, onClose, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState('');

  const handleDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/inventaire/recettes/${ing.id}`);
      onSuccess();
    } catch (err) {
      setErreur(err.response?.data?.error || 'Erreur lors de la suppression.');
      setSaving(false);
    }
  };

  return (
    <div className="inv-overlay" onClick={onClose}>
      <div className="inv-modal inv-modal--achat" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="inv-modal__header">
          <h3>Supprimer un ingrédient</h3>
          <button className="inv-modal__close" onClick={onClose} aria-label="Fermer"><Icon name="fermer" size={14} /></button>
        </div>
        <div className="inv-modal__body">
          {isLimitant && (
            <div className="inv-delete-warning" style={{ marginBottom: 16 }}>
              <p>Cet ingrédient est le <strong>facteur limitant</strong> de cette recette. Le supprimer pourrait fausser les prédictions.</p>
            </div>
          )}
          <p style={{ marginBottom: 16 }}>
            Supprimer <strong>{ing.nom_matiere}</strong> de la recette ?
          </p>
          {erreur && <p className="danger" style={{ marginBottom: 8 }}>{erreur}</p>}
          <div className="inv-modal__actions">
            <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
            <button className="btn btn-danger" onClick={handleDelete} disabled={saving}>
              {saving ? '…' : 'Supprimer quand même'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Modal Nouvelle Recette ───────────────────
function ModalNouvelleRecette({ onClose, onSuccess }) {
  const [nom, setNom] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!nom.trim()) return;
    const slug = nom.toLowerCase().trim().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') || `recette_${Date.now()}`;
    onSuccess({ produit: slug, label: nom.trim(), icon: 'package' });
  };

  return (
    <div className="inv-overlay" onClick={onClose}>
      <div className="inv-modal inv-modal--achat" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
        <div className="inv-modal__header">
          <h3>Nouvelle recette</h3>
          <button className="inv-modal__close" onClick={onClose} aria-label="Fermer"><Icon name="fermer" size={14} /></button>
        </div>
        <form className="inv-modal__body" onSubmit={handleSubmit}>
          <div className="inv-form-grid">
            <label style={{ gridColumn: '1 / -1' }}>Nom du produit
              <input type="text" placeholder="ex: Pastrami Fumé" value={nom} onChange={e => setNom(e.target.value)} required autoFocus />
            </label>
            {/* Le champ icône emoji est conservé ici car il alimente un système de données existant — à migrer séparément */}
          </div>
          <div className="inv-modal__actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary">Créer</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Modal Ajustement rapide ──────────────────
const RAISONS_ACHAT   = ['Achat fournisseur','Don reçu','Autre'];
const RAISONS_RETRAIT = ['Perte / Bris','Utilisation test','Périmé','Autre'];
const RAISONS_RESET   = ["Inventaire d'ouverture",'Inventaire physique','Correction','Remise à zéro','Autre'];

function ModalAjust({ matiere, initType, onClose, onSuccess }) {
  const [type, setType]   = useState(initType || 'achat');
  const [form, setForm]   = useState({
    quantite:          '',
    nouvelle_quantite: String(matiere?.quantite ?? ''),
    nouveau_prix:      String(matiere?.prix_unitaire ?? ''),
    raison:            "Achat fournisseur",
    notes:             '',
  });
  const [saving, setSaving]     = useState(false);
  const [erreur, setErreur]     = useState('');
  const [warning, setWarning]   = useState('');
  const [fichier, setFichier]   = useState(null);
  const [preview, setPreview]   = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFile = (file) => {
    if (!file) return;
    setFichier(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = e => setPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  // Recalcul stock après
  const stockActuel = matiere?.quantite ?? 0;
  const qteNum      = parseFloat(form.quantite) || 0;
  let stockApres    = stockActuel;
  if (type === 'achat')   stockApres = Math.round((stockActuel + qteNum)  * 1000) / 1000;
  if (type === 'retrait') stockApres = Math.max(0, Math.round((stockActuel - qteNum) * 1000) / 1000);
  if (type === 'reset')   stockApres = parseFloat(form.nouvelle_quantite) || 0;

  // Raison par défaut selon type
  const handleTypeChange = (t) => {
    setType(t);
    setErreur('');
    setWarning('');
    if (t === 'achat')   set('raison', 'Achat fournisseur');
    if (t === 'retrait') set('raison', 'Perte / Bris');
    if (t === 'reset')   set('raison', "Inventaire d'ouverture");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErreur('');
    setWarning('');
    if (type === 'achat' || type === 'retrait') {
      if (!form.quantite || qteNum <= 0) { setErreur('La quantité doit être supérieure à zéro.'); return; }
    } else if (type === 'prix') {
      if (!form.nouveau_prix || parseFloat(form.nouveau_prix) < 0) { setErreur('Prix invalide.'); return; }
    }
    setSaving(true);
    try {
      let res;
      if (type === 'achat') {
        const fd = new FormData();
        fd.append('type', type);
        fd.append('quantite', form.quantite);
        fd.append('raison', form.raison);
        fd.append('notes', form.notes);
        if (fichier) fd.append('facture', fichier);
        res = await api.post(`/inventaire/matieres/${matiere.id}/ajustement`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        const body = { type, raison: form.raison, notes: form.notes };
        if (type === 'retrait') body.quantite = form.quantite;
        else if (type === 'reset') body.nouvelle_quantite = form.nouvelle_quantite;
        else if (type === 'prix') body.nouveau_prix = form.nouveau_prix;
        res = await api.post(`/inventaire/matieres/${matiere.id}/ajustement`, body);
      }
      if (res.warning) setWarning(res.warning);
      onSuccess(res);
    } catch (err) {
      setErreur(err.response?.data?.error || "Erreur lors de l'ajustement.");
      setSaving(false);
    }
  };

  return (
    <div className="inv-overlay" onClick={onClose}>
      <div className="inv-modal inv-modal--achat" onClick={e => e.stopPropagation()}>
        <div className="inv-modal__header">
          <h3>Ajuster — {matiere?.nom}</h3>
          <button className="inv-modal__close" onClick={onClose} aria-label="Fermer"><Icon name="fermer" size={14} /></button>
        </div>
        <form className="inv-modal__body" onSubmit={handleSubmit}>
          <div className="inv-ajust-info">
            <span className="muted">Stock actuel :</span>
            <strong> {stockActuel} {matiere?.unite}</strong>
          </div>

          {/* Type selector */}
          <div className="inv-ajust-types">
            {[
              { key: 'achat',   label: 'Achat' },
              { key: 'retrait', label: 'Retrait' },
              { key: 'reset',   label: 'Reset' },
              { key: 'prix',    label: 'Modifier le prix' },
            ].map(t => (
              <label key={t.key} className={`inv-ajust-type ${type === t.key ? 'inv-ajust-type--active' : ''}`}>
                <input type="radio" name="ajust_type" value={t.key} checked={type === t.key} onChange={() => handleTypeChange(t.key)} />
                {t.label}
              </label>
            ))}
          </div>

          {/* Info contextuelle */}
          {type === 'achat' && <p className="inv-mode-info inv-mode-info--achat">Une écriture comptable sera créée automatiquement.</p>}
          {(type === 'retrait' || type === 'reset') && <p className="inv-mode-info inv-mode-info--ajustement">Aucune écriture comptable ne sera créée.</p>}
          {type === 'prix' && <p className="inv-mode-info inv-mode-info--ajustement">La valeur totale sera recalculée avec le nouveau prix.</p>}

          <div className="inv-form-grid">
            {(type === 'achat' || type === 'retrait') && (<>
              <label style={{ gridColumn: '1 / -1' }}>Quantité à {type === 'achat' ? 'ajouter' : 'retirer'} ({matiere?.unite})
                <input type="number" step="0.001" min="0.001" placeholder="ex: 5" value={form.quantite} onChange={e => set('quantite', e.target.value)} required autoFocus />
              </label>
              <label style={{ gridColumn: '1 / -1' }}>Raison
                <select value={form.raison} onChange={e => set('raison', e.target.value)}>
                  {(type === 'achat' ? RAISONS_ACHAT : RAISONS_RETRAIT).map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
            </>)}
            {type === 'reset' && (<>
              <label style={{ gridColumn: '1 / -1' }}>Nouvelle quantité ({matiere?.unite})
                <input type="number" step="0.001" min="0" value={form.nouvelle_quantite} onChange={e => set('nouvelle_quantite', e.target.value)} required autoFocus />
              </label>
              <label style={{ gridColumn: '1 / -1' }}>Raison
                <select value={form.raison} onChange={e => set('raison', e.target.value)}>
                  {RAISONS_RESET.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
            </>)}
            {type === 'prix' && (
              <label style={{ gridColumn: '1 / -1' }}>Nouveau prix ($ / {matiere?.unite_prix || matiere?.unite})
                <input type="number" step="0.01" min="0" value={form.nouveau_prix} onChange={e => set('nouveau_prix', e.target.value)} required autoFocus />
              </label>
            )}
            <label style={{ gridColumn: '1 / -1' }}>Notes
              <input type="text" placeholder="Optionnel" value={form.notes} onChange={e => set('notes', e.target.value)} />
            </label>
          </div>

          {/* Zone upload facture (achat seulement) */}
          {type === 'achat' && (
            <>
              <div
                className={`inv-drop-zone ${dragOver ? 'inv-drop-zone--over' : ''} ${fichier ? 'inv-drop-zone--has-file' : ''}`}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
                onClick={() => document.getElementById('inv-ajust-file-input').click()}
              >
                {preview
                  ? <img src={preview} alt="Aperçu facture" className="inv-drop-zone__preview" />
                  : fichier
                    ? <div className="inv-drop-zone__pdf"><Icon name="document" size={16} /> {fichier.name}</div>
                    : <div className="inv-drop-zone__placeholder"><span className="inv-drop-icon"><Icon name="televerser" size={24} /></span><p>Facture / Reçu (optionnel) — Glisser ici ou cliquer</p><small>.pdf, .jpg, .jpeg, .png — max 10 Mo</small></div>
                }
                {fichier && (
                  <button type="button" className="inv-drop-zone__remove" onClick={e => { e.stopPropagation(); setFichier(null); setPreview(null); }} aria-label="Retirer"><Icon name="fermer" size={12} /></button>
                )}
                <input id="inv-ajust-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
                <input id="inv-ajust-camera" type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
              </div>
              <div className="inv-mobile-upload">
                <button type="button" className="btn btn-ghost btn--sm" onClick={() => document.getElementById('inv-ajust-camera').click()}><Icon name="photo" size={14} /> Prendre une photo</button>
                <button type="button" className="btn btn-ghost btn--sm" onClick={() => document.getElementById('inv-ajust-file-input').click()}><Icon name="document" size={14} /> Choisir un fichier</button>
              </div>
            </>
          )}

          {/* Aperçu stock après */}
          {type !== 'prix' && (
            <div className="inv-stock-apercu">
              <span className="muted">Stock après :</span>
              <strong className={stockApres < stockActuel ? 'danger' : stockApres > stockActuel ? 'success' : ''}> {stockApres} {matiere?.unite}</strong>
            </div>
          )}

          {warning && <p className="warning" style={{ marginTop: 8, fontSize: 12 }}>{warning}</p>}
          {erreur  && <p className="danger"  style={{ marginTop: 8 }}>{erreur}</p>}

          <div className="inv-modal__actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Enregistrement…' : 'Confirmer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Modal Suppression ────────────────────────
function ModalSupprimer({ matiere, onClose, onSuccess, onArchiver }) {
  const [loading, setLoading]   = useState(true);
  const [nbRecettes, setNbRecettes] = useState(0);
  const [erreur, setErreur]     = useState('');
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    // On essaie une suppression pour connaître le nb_recettes
    api.delete(`/inventaire/matieres/${matiere.id}`)
      .then(() => { setLoading(false); onSuccess(); })
      .catch(err => {
        setLoading(false);
        if (err.response?.status === 409) {
          setNbRecettes(err.response.data.nb_recettes || 0);
        } else {
          setErreur(err.response?.data?.error || 'Erreur.');
        }
      });
  }, []); // eslint-disable-line

  const handleForceSupprimer = async () => {
    // Cette branche ne s'atteint que si nbRecettes > 0 (la suppression normale l'aurait déjà faite)
    // On propose d'archiver à la place — bouton "Supprimer quand même" archive avec un flag
    // Pour l'instant, on n'expose pas de suppression forcée des recettes — on redirige vers archiver
    await handleArchiver();
  };

  const handleArchiver = async () => {
    setSaving(true);
    try {
      await api.patch(`/inventaire/matieres/${matiere.id}/archiver`);
      onArchiver();
    } catch (err) {
      setErreur(err.response?.data?.error || "Erreur lors de l'archivage.");
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="inv-overlay">
      <div className="inv-modal inv-modal--achat" style={{ padding: 24 }}>
        <p className="muted" style={{ textAlign: 'center' }}>Vérification…</p>
      </div>
    </div>
  );

  return (
    <div className="inv-overlay" onClick={onClose}>
      <div className="inv-modal inv-modal--achat" onClick={e => e.stopPropagation()}>
        <div className="inv-modal__header">
          <h3>Supprimer — {matiere?.nom}</h3>
          <button className="inv-modal__close" onClick={onClose} aria-label="Fermer"><Icon name="fermer" size={14} /></button>
        </div>
        <div className="inv-modal__body">
          {erreur && <p className="danger">{erreur}</p>}
          {nbRecettes > 0 ? (<>
            <div className="inv-delete-warning">
              <p>Cette matière est utilisée dans <strong>{nbRecettes} recette{nbRecettes > 1 ? 's' : ''}</strong>.</p>
              <p className="muted" style={{ marginTop: 8 }}>Recommandé : archiver plutôt que supprimer pour préserver l'historique.</p>
            </div>
            <div className="inv-modal__actions">
              <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
              <button className="btn btn-ghost" style={{ color: 'var(--warning)' }} onClick={handleArchiver} disabled={saving}>{saving ? '…' : 'Archiver'}</button>
              <button className="btn btn-danger" onClick={handleForceSupprimer} disabled={saving}>{saving ? '…' : 'Archiver quand même'}</button>
            </div>
          </>) : (<>
            <p style={{ marginBottom: 16 }}>La suppression a été effectuée.</p>
            <div className="inv-modal__actions">
              <button className="btn btn-primary" onClick={onClose}>Fermer</button>
            </div>
          </>)}
        </div>
      </div>
    </div>
  );
}

// ─── Drawer Détail Matière ────────────────────
function DrawerDetail({ matiere, onClose, onAchat }) {
  const { data, loading } = useApi(matiere ? `/inventaire/matieres/${matiere.id}` : null);
  if (!matiere) return null;
  const detail = data || matiere;

  return (
    <div className="inv-drawer-backdrop" onClick={onClose}>
      <div className="inv-drawer" onClick={e => e.stopPropagation()}>
        <div className="inv-drawer__header">
          <div>
            <h3>{detail.nom}</h3>
            <span className="muted">{detail.categorie} · {detail.unite}</span>
          </div>
          <button className="inv-modal__close" onClick={onClose} aria-label="Fermer"><Icon name="fermer" size={14} /></button>
        </div>
        <div className="inv-drawer__body">
          <div className="inv-drawer__stats">
            <div className="inv-stat"><span className="muted">Stock actuel</span><strong>{detail.quantite} {detail.unite}</strong></div>
            <div className="inv-stat"><span className="muted">Valeur</span><strong className="gold">{(detail.quantite * (detail.prix_unitaire || 0)).toFixed(2)} $</strong></div>
            <div className="inv-stat"><span className="muted">Prix unitaire</span><strong>{detail.prix_unitaire || 0} $ / {detail.unite_prix || detail.unite}</strong></div>
            <div className="inv-stat"><span className="muted">Seuil alerte</span><strong>{detail.seuil_alerte} {detail.unite}</strong></div>
          </div>
          {detail.fournisseur && (<p className="muted" style={{ marginBottom: 4 }}>Fournisseur habituel : <strong style={{ color: 'var(--text)' }}>{detail.fournisseur}</strong></p>)}
          {detail.derniere_commande && (<p className="muted" style={{ marginBottom: 16 }}>Dernier achat : <strong style={{ color: 'var(--text)' }}>{detail.derniere_commande}</strong></p>)}
          {data?.recettes?.length > 0 && (
            <div className="inv-drawer__section">
              <h4>Utilisé dans</h4>
              <div className="inv-tags">{data.recettes.map((r, i) => <span key={i} className="inv-tag">{PRODUIT_LABEL[r.produit] || r.produit}</span>)}</div>
            </div>
          )}
          <div className="inv-drawer__section">
            <div className="inv-drawer__section-header">
              <h4>Historique</h4>
              <button className="btn btn-primary btn--sm" onClick={() => onAchat(matiere)}><Icon name="ajouter" size={13} /> Achat / Ajustement</button>
            </div>
            {loading && <p className="muted">Chargement…</p>}
            {!loading && !data?.achats?.length && <p className="muted">Aucune entrée enregistrée.</p>}
            {data?.achats?.length > 0 && (
              <table className="esc-table inv-drawer__table">
                <thead><tr><th>Date</th><th>Type</th><th>Qté</th><th>Prix/u</th><th>Total</th><th>Détail</th><th>Facture</th></tr></thead>
                <tbody>
                  {data.achats.map(a => (
                    <tr key={a.id}>
                      <td>{a.date_achat}</td>
                      <td>{a.type === 'ajustement' || a.type === 'retrait' || a.type === 'reset' || a.type === 'prix'
                        ? <span className="inv-badge inv-badge--ajustement">{a.type}</span>
                        : <span className="inv-badge inv-badge--achat">Achat</span>
                      }</td>
                      <td className={a.quantite < 0 ? 'danger' : ''}>{a.quantite > 0 ? '+' : ''}{a.quantite} {detail.unite}</td>
                      <td>{a.type !== 'ajustement' && a.type !== 'retrait' && a.type !== 'reset' ? `${a.prix_unitaire}$` : '—'}</td>
                      <td className={a.type === 'ajustement' || a.type === 'retrait' || a.type === 'reset' ? 'muted' : 'gold'}>
                        {a.type === 'achat' ? `${a.cout_total}$` : '—'}
                      </td>
                      <td className="muted">{a.raison || a.fournisseur || '—'}</td>
                      <td>{a.facture_fichier ? (
                        a.facture_type === 'pdf'
                          ? <a href={uploadUrl(`/uploads/factures/${a.facture_fichier}`)} target="_blank" rel="noreferrer" className="inv-facture-link">PDF</a>
                          : <a href={uploadUrl(`/uploads/factures/${a.facture_fichier}`)} target="_blank" rel="noreferrer"><img src={uploadUrl(`/uploads/factures/${a.facture_fichier}`)} alt="Facture" className="inv-facture-thumb" /></a>
                      ) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Composant principal ──────────────────────
export default function InventaireMatieres() {
  const { data, loading, error, refetch } = useApi('/inventaire/matieres');
  const { data: predData, loading: predLoading, refetch: refetchPred } = useApi('/inventaire/prediction');

  const [onglet, setOnglet]               = useState('tous');
  const [drawerItem, setDrawerItem]       = useState(null);
  const [achatItem, setAchatItem]         = useState(null);
  const [modalCreer, setModalCreer]       = useState(false);
  const [editItem, setEditItem]           = useState(null);
  const [ajustItem, setAjustItem]         = useState(null);
  const [ajustInitType, setAjustInitType] = useState('achat');
  const [deleteItem, setDeleteItem]       = useState(null);
  const [openMenu, setOpenMenu]           = useState(null);
  const [modalPredProduit, setModalPredProduit] = useState(null);

  // ─── État onglet page + recettes ─────────────
  const [pageOnglet, setPageOnglet]               = useState('inventaire');
  const [recettes, setRecettes]                   = useState(null);
  const [recettesLoading, setRecettesLoading]     = useState(false);
  const [recetteProduit, setRecetteProduit]       = useState('saucisses');
  const [modalAjoutIng, setModalAjoutIng]         = useState(false);
  const [modalEditIng, setModalEditIng]           = useState(null);
  const [confirmDeleteIng, setConfirmDeleteIng]   = useState(null);
  const [modalNouvelleRecette, setModalNouvelleRecette] = useState(false);
  const [extraProduits, setExtraProduits]         = useState([]);
  const [toast, setToast]                         = useState(null);

  // ModalPred dérivé des données live (se met à jour après refetch)
  const modalPred = modalPredProduit
    ? predData?.predictions?.find(p => p.produit === modalPredProduit) || null
    : null;

  // Fermer dropdown si clic dehors
  const menuRef = useRef(null);
  useEffect(() => {
    if (!openMenu) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openMenu]);

  const refetchAll = useCallback(() => { refetch(); refetchPred(); }, [refetch, refetchPred]);

  const fetchRecettes = useCallback(async () => {
    setRecettesLoading(true);
    try {
      const data = await api.get('/inventaire/recettes');
      setRecettes(data);
    } catch (err) {
      console.error(err);
    } finally {
      setRecettesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (pageOnglet === 'recettes' && recettes === null) fetchRecettes();
  }, [pageOnglet, recettes, fetchRecettes]);

  const handleRecetteChanged = useCallback(async () => {
    setModalAjoutIng(false);
    setModalEditIng(null);
    setConfirmDeleteIng(null);
    await fetchRecettes();
    refetchPred();
    setToast({ msg: 'Recette mise à jour — prédictions recalculées', type: 'success' });
  }, [fetchRecettes, refetchPred]);

  const handleAchatSuccess = useCallback(() => { setAchatItem(null); refetchAll(); }, [refetchAll]);
  const handleCreerSuccess  = useCallback(() => { setModalCreer(false); refetchAll(); }, [refetchAll]);
  const handleEditSuccess   = useCallback(() => { setEditItem(null); refetchAll(); }, [refetchAll]);
  const handleAjustSuccess  = useCallback((result) => {
    setAjustItem(null);
    refetchAll();

    let msg = 'Ajustement enregistré';
    if (result?.type === 'reset') {
      msg = `Stock mis à jour — ${result?.raison || "Inventaire d'ouverture"}`;
    } else if (result?.type === 'achat') {
      msg = result?.facture ? 'Achat enregistré — Facture attachée ✓' : 'Achat enregistré';
    } else if (result?.type === 'retrait') {
      msg = 'Retrait enregistré';
    } else if (result?.type === 'prix') {
      msg = 'Prix mis à jour';
    }

    setToast({ msg, type: 'success' });
  }, [refetchAll]);
  const handleDeleteSuccess = useCallback(() => { setDeleteItem(null); refetchAll(); }, [refetchAll]);
  const handleArchiverSuccess = useCallback(() => { setEditItem(null); setDeleteItem(null); setOpenMenu(null); refetchAll(); }, [refetchAll]);

  const ouvrirAchat = (item)  => { setDrawerItem(null); setAchatItem(item); };
  const ouvrirAjust = (item, type = 'achat') => { setOpenMenu(null); setAjustItem(item); setAjustInitType(type); };
  const toggleMenu  = (id)    => setOpenMenu(prev => prev === id ? null : id);

  const archiverDirect = async (item) => {
    setOpenMenu(null);
    try { await api.patch(`/inventaire/matieres/${item.id}/archiver`); refetchAll(); }
    catch (err) { console.error(err); }
  };

  const restaurer = async (item) => {
    try { await api.patch(`/inventaire/matieres/${item.id}/restaurer`); refetchAll(); }
    catch (err) { console.error(err); }
  };

  // Depuis le modal de prédiction — ouvre l'ajustement pour l'ingrédient
  const ouvrirAjustDepuisPrediction = useCallback((matiereId) => {
    const matiere = data?.tous?.find(m => m.id === matiereId);
    if (matiere) { setAjustItem(matiere); setAjustInitType('achat'); }
  }, [data]);

  // Produits visibles dans la grille recettes (statiques + extras + inconnus en DB)
  const allProduits = (() => {
    const result = [...PRODUITS_RECETTES];
    for (const extra of extraProduits) {
      if (!result.find(p => p.produit === extra.produit)) result.push(extra);
    }
    if (recettes) {
      for (const key of Object.keys(recettes)) {
        if (!result.find(p => p.produit === key)) result.push({ produit: key, label: key, icon: 'package' });
      }
    }
    return result;
  })();

  const items     = data ? (onglet === 'tous' ? (data.tous || []) : (data.categories?.[onglet] || [])) : [];
  const archivees = data?.archivees || [];
  const nbAlertes = data?.alertes?.length || 0;

  // ─── Onglets page ──────────────────────────
  const renderPageTabs = () => (
    <div className="inv-page-tabs">
      {[
        { key: 'inventaire',  label: 'Inventaire' },
        { key: 'predictions', label: 'Prédictions' },
        { key: 'recettes',    label: 'Recettes' },
        { key: 'archives',    label: `Archivés${archivees.length > 0 ? ` (${archivees.length})` : ''}` },
      ].map(t => (
        <button
          key={t.key}
          className={`inv-page-tab ${pageOnglet === t.key ? 'inv-page-tab--active' : ''}`}
          onClick={() => setPageOnglet(t.key)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );

  // ─── Barre résumé ──────────────────────────
  const renderBarre = () => (
    <div className="inv-barre rise-in">
      <div className="inv-barre__card">
        <span className="inv-barre__label">Valeur totale inventaire</span>
        <span className="inv-barre__value inv-barre__value--accent">{data?.valeur_totale?.toFixed(2) ?? '—'} $</span>
      </div>
      <div className="inv-barre__card">
        <span className="inv-barre__label">Alertes stock</span>
        <span className={`inv-barre__value ${nbAlertes > 0 ? 'inv-barre__value--danger' : 'inv-barre__value--success'}`}>
          {nbAlertes} item{nbAlertes !== 1 ? 's' : ''} sous seuil
        </span>
      </div>
      <div className="inv-barre__card">
        <span className="inv-barre__label">Matières actives</span>
        <span className="inv-barre__value">{data?.tous?.length ?? '—'}</span>
      </div>
    </div>
  );

  // ─── Prédictions ───────────────────────────
  const renderPredictions = () => {
    if (predLoading) return (
      <div className="inv-pred-grid">
        {[0,1,2,3,4,5].map(i => (
          <div key={i} className="skeleton" style={{ height: 180, borderRadius: 'var(--radius-lg)', animationDelay: `${i * 60}ms` }} />
        ))}
      </div>
    );
    if (!predData?.predictions?.length) return (
      <div className="empty-state">
        <Icon name="analytics" size={40} />
        <p className="empty-state__titre">Aucune recette configurée</p>
        <p>Configurez les recettes dans l'onglet Recettes pour voir les prédictions.</p>
      </div>
    );
    return (
      <section className="inv-section">
        <div className="inv-section__title-row">
          <h2 className="inv-section__title">Batches possibles avec le stock actuel</h2>
        </div>
        <div className="inv-pred-grid">
          {predData.predictions.map(pred => (
            <div key={pred.produit} className={`inv-pred-card inv-pred-card--${couleurBatch(pred.nb_batches_possible)} rise-in`}
              onClick={() => setModalPredProduit(pred.produit)} role="button" tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && setModalPredProduit(pred.produit)}>
              <div className="inv-pred-card__icon">
                <Icon name={PRODUIT_ICON[pred.produit] || 'package'} size={24} />
              </div>
              <div className="inv-pred-card__produit">{PRODUIT_LABEL[pred.produit]}</div>
              <div className="inv-pred-card__count">{pred.nb_batches_possible}</div>
              <div className="inv-pred-card__label">batch{pred.nb_batches_possible !== 1 ? 'es' : ''} possible{pred.nb_batches_possible !== 1 ? 's' : ''}</div>
              {pred.facteur_limitant ? (
                <div className="inv-pred-card__limite">
                  Limité par : {pred.facteur_limitant}
                  <br /><small>{pred.facteur_limitant_stock} dispo · {pred.facteur_limitant_besoin}</small>
                </div>
              ) : (
                <div className="inv-pred-card__ok"><Icon name="succes" size={14} /> Stock suffisant</div>
              )}
            </div>
          ))}
        </div>
        {predData.liste_achats_suggeree?.length > 0 && (
          <div className="inv-achats-suggeres">
            <div className="inv-achats-header">
              <span className="card-header-icon"><Icon name="liste" size={16} /></span>
              <h3 className="inv-achats-title">Liste d'achats suggérée</h3>
            </div>
            <div className="inv-table-wrap">
              <table className="esc-table">
                <thead><tr><th>Ingrédient</th><th>Quantité à acheter</th><th>Pour débloquer</th><th>Priorité</th></tr></thead>
                <tbody>
                  {predData.liste_achats_suggeree.map((a, i) => (
                    <tr key={i}>
                      <td>{a.nom}</td>
                      <td className="gold" style={{ fontFeatureSettings: "'tnum' 1" }}>{a.quantite} {a.unite}</td>
                      <td className="muted">{a.pour.map(p => PRODUIT_LABEL[p] || p).join(', ')}</td>
                      <td><span className="badge badge--danger">Haute</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    );
  };

  // ─── Tableau inventaire ─────────────────────
  const renderInventaire = () => (
    <section className="inv-section">
      <div className="inv-section__title-row">
        <h2 className="inv-section__title">Inventaire des matières premières</h2>
        <button className="btn btn-primary btn--sm" onClick={() => setModalCreer(true)}><Icon name="ajouter" size={13} /> Nouvelle matière</button>
      </div>

      <div className="inv-tabs">
        {CATEGORIES.filter(c => c.key === 'tous' || data?.categories?.[c.key]?.length > 0).map(c => (
          <button key={c.key} className={`inv-tab ${onglet === c.key ? 'inv-tab--active' : ''}`} onClick={() => setOnglet(c.key)}>
            {c.label}
            {c.key !== 'tous' && data?.categories?.[c.key] && <span className="inv-tab__count">{data.categories[c.key].length}</span>}
          </button>
        ))}
      </div>

      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {[0,1,2,3,4,5].map(i => (
            <div key={i} className="skeleton" style={{ height: 44, borderRadius: 'var(--radius-sm)', animationDelay: `${i * 50}ms` }} />
          ))}
        </div>
      )}
      {error && (
        <div className="error-inline">
          <Icon name="erreur" size={16} />
          <span>Erreur : {error}</span>
          <button className="btn btn-ghost btn--sm" style={{ marginLeft: 'auto' }} onClick={() => refetch()}>Réessayer</button>
        </div>
      )}

      {!loading && !error && (
        <div className="inv-table-wrap" ref={openMenu ? menuRef : null}>
          <table className="esc-table">
            <thead>
              <tr><th>Ingrédient</th><th>Catégorie</th><th>Stock</th><th>Valeur</th><th>Seuil</th><th>Statut</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 0 }}>
                    <div className="empty-state">
                      <Icon name="package" size={36} />
                      <p className="empty-state__titre">Aucune matière dans cette catégorie</p>
                      <p>Utilisez le bouton "Nouvelle matière" pour en ajouter.</p>
                    </div>
                  </td>
                </tr>
              )}
              {items.map(item => (
                <tr key={item.id}
                  className={`inv-item-row ${item.seuil_alerte > 0 && item.quantite <= item.seuil_alerte ? 'inv-item-row--alerte' : ''}`}
                  onClick={() => setDrawerItem(item)}
                  style={{ cursor: 'pointer' }}>
                  <td data-label="Ingrédient"><strong>{item.nom}</strong></td>
                  <td data-label="Catégorie" className="muted">{item.categorie}</td>
                  <td data-label="Stock" style={{ fontFeatureSettings: "'tnum' 1" }}>{item.quantite} {item.unite}</td>
                  <td data-label="Valeur" className="gold" style={{ fontFeatureSettings: "'tnum' 1" }}>{(item.quantite * (item.prix_unitaire || 0)).toFixed(2)} $</td>
                  <td data-label="Seuil" className="muted" style={{ fontFeatureSettings: "'tnum' 1" }}>{item.seuil_alerte} {item.unite}</td>
                  <td data-label="Statut">{statutBadge(item)}</td>
                  <td data-label="Actions" onClick={e => e.stopPropagation()}>
                    <div className="inv-row-actions">
                      <button className="btn btn-ghost btn--sm inv-btn-edit" title="Modifier les infos" onClick={() => setEditItem(item)}>
                        <Icon name="modifier" size={13} />
                      </button>
                      <div className="inv-menu-wrap">
                        <button className="btn btn-ghost btn--sm" title="Plus d'options" onClick={() => toggleMenu(item.id)}>···</button>
                        {openMenu === item.id && (
                          <div className="inv-dropdown">
                            <button className="inv-dropdown__item" onClick={() => { setEditItem(item); setOpenMenu(null); }}>Modifier les infos</button>
                            <button className="inv-dropdown__item" onClick={() => ouvrirAjust(item, 'achat')}>Ajuster la quantité</button>
                            <button className="inv-dropdown__item" onClick={() => ouvrirAjust(item, 'prix')}>Modifier le prix</button>
                            <div className="inv-dropdown__divider" />
                            <button className="inv-dropdown__item inv-dropdown__item--warning" onClick={() => archiverDirect(item)}>Archiver</button>
                            <button className="inv-dropdown__item inv-dropdown__item--danger" onClick={() => { setDeleteItem(item); setOpenMenu(null); }}>Supprimer</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </section>
  );

  // ─── Archives ──────────────────────────────
  const renderArchives = () => (
    <section className="inv-section">
      <h2 className="inv-section__title">Matières archivées</h2>
      {archivees.length === 0 ? (
        <div className="empty-state">
          <Icon name="package" size={36} />
          <p className="empty-state__titre">Aucune matière archivée</p>
        </div>
      ) : (
        <table className="esc-table">
          <thead><tr><th>Nom</th><th>Catégorie</th><th>Stock</th><th>Unité</th><th></th></tr></thead>
          <tbody>
            {archivees.map(item => (
              <tr key={item.id} className="inv-archivees__row">
                <td className="muted"><strong>{item.nom}</strong></td>
                <td className="muted">{item.categorie}</td>
                <td className="muted">{item.quantite}</td>
                <td className="muted">{item.unite}</td>
                <td><button className="btn btn-ghost btn--sm" onClick={() => restaurer(item)}>Restaurer</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );

  // ─── Recettes ──────────────────────────────
  const renderRecettes = () => {
    if (recettesLoading) return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 'var(--space-4)' }}>
        {[0,1,2,3,4,5].map(i => (
          <div key={i} className="skeleton" style={{ height: 150, borderRadius: 'var(--radius-lg)', animationDelay: `${i * 50}ms` }} />
        ))}
      </div>
    );

    const ings        = recettes?.[recetteProduit] || [];
    const activeProd  = allProduits.find(p => p.produit === recetteProduit);
    const predProduit = predData?.predictions?.find(p => p.produit === recetteProduit);
    const facteurLimitantNom = predProduit?.facteur_limitant || null;
    const limitantId  = facteurLimitantNom
      ? ings.find(i => i.nom_matiere === facteurLimitantNom)?.matiere_id
      : null;

    return (
      <section className="inv-section">
        <h2 className="inv-section__title">Recettes — Ingrédients par produit</h2>

        <div className="recettes-grid">
          {allProduits.map(p => {
            const nb = recettes?.[p.produit]?.length || 0;
            return (
              <div
                key={p.produit}
                className={`recette-card ${recetteProduit === p.produit ? 'active' : ''}`}
                onClick={() => setRecetteProduit(p.produit)}
                role="button" tabIndex={0}
                onKeyDown={e => e.key === 'Enter' && setRecetteProduit(p.produit)}
              >
                <span className="recette-icon">
                  <Icon name={p.icon || 'package'} size={28} />
                </span>
                <span className="recette-nom">{p.label}</span>
                <span className="recette-nb">{nb} ingrédient{nb !== 1 ? 's' : ''}</span>
              </div>
            );
          })}
          <div
            className="recette-card add-card"
            onClick={() => setModalNouvelleRecette(true)}
            role="button" tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && setModalNouvelleRecette(true)}
          >
            <span className="recette-add-icon">+</span>
            <span className="recette-nom">Nouvelle recette</span>
          </div>
        </div>

        <div className="inv-section__title-row" style={{ marginBottom: 16 }}>
          <h3 className="inv-section__title" style={{ margin: 0 }}>
            Recette — {activeProd?.label || recetteProduit}
          </h3>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button className="btn btn-ghost btn--sm" onClick={fetchRecettes} title="Actualiser les recettes"><Icon name="rafraichir" size={13} /> Actualiser</button>
            <button className="btn btn-primary btn--sm" onClick={() => setModalAjoutIng(true)}>
              <Icon name="ajouter" size={13} /> Ajouter un ingrédient
            </button>
          </div>
        </div>

        {ings.length === 0 ? (
          <div className="empty-state">
            <Icon name="liste" size={36} />
            <p className="empty-state__titre">Aucun ingrédient dans cette recette</p>
            <p>Cliquez sur "Ajouter un ingrédient" pour commencer.</p>
          </div>
        ) : (
          <div className="inv-table-wrap">
            <table className="esc-table">
              <thead>
                <tr><th>Ingrédient</th><th>Quantité / batch</th><th>Type</th><th>Notes</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {ings.map(ing => {
                  const manquante = !ing.nom_matiere || ing.matiere_active === 0;
                  return (
                    <tr key={ing.id} style={manquante ? { opacity: 0.5 } : undefined}>
                      <td>
                        {manquante ? (
                          <span style={{ color: 'var(--warning)', display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <Icon name="attention" size={13} />
                            Ingrédient non trouvé dans l'inventaire
                            <button
                              className="btn btn-ghost btn--sm"
                              style={{ marginLeft: 'var(--space-2)' }}
                              onClick={() => setModalCreer(true)}
                            >Créer cet ingrédient</button>
                          </span>
                        ) : (
                          <>
                            <strong>{ing.nom_matiere}</strong>
                            {ing.matiere_id === limitantId && (
                              <span className="inv-badge inv-badge--bas" style={{ marginLeft: 'var(--space-2)' }}>Limitant</span>
                            )}
                          </>
                        )}
                      </td>
                      <td>
                        {ing.type_calcul === 'pct_viande'
                          ? `${ing.pct_base}%`
                          : `${ing.quantite_par_batch} ${ing.unite}`}
                      </td>
                      <td className="muted">
                        {ing.type_calcul === 'fixe'       && 'Fixe'}
                        {ing.type_calcul === 'pct_viande' && '% viande'}
                        {ing.type_calcul === 'par_unite'  && 'Par unité'}
                      </td>
                      <td className="muted">{ing.notes || '—'}</td>
                      <td>
                        <div className="inv-row-actions">
                          <button className="btn btn-ghost btn--sm inv-btn-edit" onClick={() => setModalEditIng(ing)} title="Modifier" disabled={manquante}><Icon name="modifier" size={13} /></button>
                          <button className="btn btn-ghost btn--sm" style={{ color: 'var(--danger)' }} onClick={() => setConfirmDeleteIng(ing)} title="Supprimer"><Icon name="supprimer" size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {confirmDeleteIng && (
          <ConfirmDeleteIngredient
            ing={confirmDeleteIng}
            isLimitant={confirmDeleteIng?.matiere_id === limitantId}
            onClose={() => setConfirmDeleteIng(null)}
            onSuccess={handleRecetteChanged}
          />
        )}
      </section>
    );
  };

  return (
    <div className="inv-page">
      <div className="inv-page__header">
        <div>
          <h1 className="inv-page__title">Inventaire — Matières Premières</h1>
          <p className="inv-page__subtitle">Stocks, recettes et prédictions de production</p>
        </div>
      </div>

      {renderBarre()}
      {renderPageTabs()}

      {pageOnglet === 'inventaire'  && renderInventaire()}
      {pageOnglet === 'predictions' && renderPredictions()}
      {pageOnglet === 'recettes'    && renderRecettes()}
      {pageOnglet === 'archives'    && renderArchives()}

      {drawerItem && <DrawerDetail matiere={drawerItem} onClose={() => setDrawerItem(null)} onAchat={ouvrirAchat} />}
      {achatItem && <ModalAchat matiere={achatItem} onClose={() => setAchatItem(null)} onSuccess={handleAchatSuccess} />}
      {modalCreer && <ModalCreer onClose={() => setModalCreer(false)} onSuccess={handleCreerSuccess} />}
      {editItem && <ModalEditInfos matiere={editItem} onClose={() => setEditItem(null)} onSuccess={handleEditSuccess} onArchiver={handleArchiverSuccess} />}
      {ajustItem && <ModalAjust matiere={ajustItem} initType={ajustInitType} onClose={() => setAjustItem(null)} onSuccess={handleAjustSuccess} />}
      {deleteItem && <ModalSupprimer matiere={deleteItem} onClose={() => setDeleteItem(null)} onSuccess={handleDeleteSuccess} onArchiver={handleArchiverSuccess} />}

      {modalPred && (
        <ModalPrediction
          pred={modalPred}
          onClose={() => setModalPredProduit(null)}
          onAjustIngredient={ouvrirAjustDepuisPrediction}
        />
      )}

      {modalAjoutIng && (
        <ModalRecetteIngredient
          produit={recetteProduit}
          produitLabel={allProduits.find(p => p.produit === recetteProduit)?.label || recetteProduit}
          item={null}
          matieres={data?.tous}
          onClose={() => setModalAjoutIng(false)}
          onSuccess={handleRecetteChanged}
        />
      )}
      {modalEditIng && (
        <ModalRecetteIngredient
          produit={recetteProduit}
          produitLabel={allProduits.find(p => p.produit === recetteProduit)?.label || recetteProduit}
          item={modalEditIng}
          matieres={data?.tous}
          onClose={() => setModalEditIng(null)}
          onSuccess={handleRecetteChanged}
        />
      )}
      {modalNouvelleRecette && (
        <ModalNouvelleRecette
          onClose={() => setModalNouvelleRecette(false)}
          onSuccess={({ produit, label }) => {
            setExtraProduits(prev => [...prev, { produit, label, icon: 'package' }]);
            setRecetteProduit(produit);
            setModalNouvelleRecette(false);
          }}
        />
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onDismiss={() => setToast(null)} />}

      <button className="fab" onClick={() => setModalCreer(true)} aria-label="Nouvelle matière" title="Nouvelle matière">+</button>
    </div>
  );
}
